/* eslint-disable react/prop-types */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { getAddresses } from "../components/AddressComponent/services/addressService.js";
import { useMemo } from "react";
import axios from "axios";
import store from "../store";
import { setTokenExpired } from "../Redux/authSlice";
import { getCookie } from "../lib/cookies";
import {
  getCustomerFromMemory,
  setCustomerInMemory,
  setWhitelabelInMemory,
  subscribeSessionMemory,
} from "../lib/sharedSessionMemory";

const UserContext = createContext("");

export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState([]);
  const [user, setUser] = useState([]);
  const [addressesData, setAddressesData] = useState([]);
  const [addressShipping, setAddressShipping] = useState([]);
  const [updateAddresses, setUpdateAddresses] = useState();
  const [updateUser, setUpdateUser] = useState();
  const [cartItemsError, setCartItemsError] = useState({});
  const [haveAddressShippingRequest, setHaveAddressShippingRequest] =
    useState(null);
  const [mainAddress, setMainAddress] = useState();
  const [mainCard, setMainCard] = useState();

  const location = useLocation();
  const currentUrl = location.pathname; //

  const companyData = useMemo(() => {
    return user?.company;
  }, [user]);

  const isPJ = useMemo(() => {
    return !!user?.company?.uuid;
  }, [user]);

  async function updateUserData() {
    let clientUserData = getCustomerFromMemory();
    
    if (!clientUserData || clientUserData === "null" || clientUserData === "{}") {
      try {
        const token = getCookie("wlt");
        if (!token) {
          console.error("Token não encontrado, não é possível obter customer");
          store.dispatch(setTokenExpired(true));
          return null;
        }

        const API_URL_CREDENCIAIS = `${process.env.REACT_APP_API_AUTHORIZATION}/v1`;
        let channel = process.env.REACT_APP_DEFAULT_CHANNEL;
        
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          channel = payload?.channel ?? channel;
        } catch (err) {
          console.warn('Falha ao decodificar token para obter channel:', err);
        }

        const { data: authData } = await axios.post(
          `${API_URL_CREDENCIAIS}/jwt/validate-channel`,
          { channel, token }
        );

        if (authData?.success) {
          setCustomerInMemory(authData);
          const whitelabelData =
            authData?.whitelabel ||
            authData?.data?.whitelabel ||
            authData?.data?.config?.whitelabel;

          if (whitelabelData) {
            setWhitelabelInMemory(whitelabelData);
          }
          clientUserData = authData;
          store.dispatch(setTokenExpired(false));
        } else {
          store.dispatch(setTokenExpired(true));
          return null;
        }
      } catch (error) {
        console.error("Erro ao obter customer:", error);
        if (error.response?.status === 401) {
          console.warn("Token expirado. Sessão expirada.");
        }
        store.dispatch(setTokenExpired(true));
        return null;
      }
    }

    try {
      const user =
        typeof clientUserData === "string"
          ? JSON.parse(clientUserData)
          : clientUserData;
      
      if (!user || !user.data) {
        console.error("Dados do customer inválidos");
        return null;
      }

      setUserData(user);
      setUser(user?.data);
      setUpdateAddresses(user?.data?.addresses);
      setAddressShipping(user?.data?.addresses?.[0]);

      return user.data;
    } catch (error) {
      console.error("Erro ao processar dados do customer:", error);
      return null;
    }
  }

  const changeAddress = useCallback((address) => {
    setHaveAddressShippingRequest(false);
    setAddressShipping(address);
    setHaveAddressShippingRequest(true);
  }, []);

  useEffect(() => {
    const refreshAddresses = async () => {
      if (!user?.cpf) return;
      const addresses = await getAddresses(user.cpf);
      setAddressesData(addresses);
    };

    if (updateAddresses && user?.cpf) refreshAddresses();
  }, [updateAddresses, user?.cpf]);

  useEffect(() => {
    const refreshUser = async () => {
      let clientUserData = getCustomerFromMemory();
      if (typeof clientUserData === "string") {
        clientUserData = JSON.parse(clientUserData);
      }
      setCustomerInMemory(clientUserData);
      updateUserData();
    };

    if (updateUser) refreshUser();
  }, [updateUser]);

  useEffect(() => {
    const initializeUserData = () => {
      try {
        const clientUserData = getCustomerFromMemory();
        if (clientUserData) {
          const user =
            typeof clientUserData === "string"
              ? JSON.parse(clientUserData)
              : clientUserData;
          if (user && user.data) {
            setUserData(user);
            setUser(user.data);
            setUpdateAddresses(user.data.addresses);
            setAddressShipping(user.data.addresses[0]);
          }
        }
      } catch (error) {
        console.error("Erro ao carregar dados do usuário:", error);
      }
    };

    initializeUserData();
    const unsubscribe = subscribeSessionMemory((memory) => {
      if (!memory.customer || !memory.customer.data) return;
      setUserData(memory.customer);
      setUser(memory.customer.data);
      setUpdateAddresses(memory.customer.data.addresses);
      setAddressShipping(memory.customer.data.addresses?.[0]);
    });
    return unsubscribe;
  }, []);

  return (
    <UserContext.Provider
      value={{
        userData,
        setUserData,
        setUser,
        user,
        updateUserData,
        changeAddress,
        addressShipping,
        addressesData,
        mainAddress,
        setMainAddress,
        setUpdateAddresses,
        setUpdateUser,
        mainCard,
        setMainCard,
        haveAddressShippingRequest,
        cartItemsError,
        setCartItemsError,
        isPJ,
        companyData,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export function useUserData() {
  return useContext(UserContext);
}

UserProvider.propTypes = {
  children: PropTypes.node,
};
